import network from '@/services/network';
import { ApiResponse } from '@/types';
import { AvailabilityRangeResponse, AvailabilityResponse, ContactSearchResult, RcWeiche, WizardZeitenAntwort } from '@/types/callcenter';
import { Buchungsmodus } from '@/store/callcenter/buchungsmodus';
import { ErrorResponse } from '@/utils';

const searchCustomerByContact = async (email?: string, phone?: string): Promise<ContactSearchResult> => {
  try {
    const payload: Record<string, string> = {};
    if (email) payload.email = email;
    if (phone) payload.phone = phone;

    if (Object.keys(payload).length === 0) {
      return { matched: false, customer: null, match_reasons: [] };
    }

    const { data } = await network.post<ContactSearchResult>('/customers/contact-search', payload);
    return data;
  } catch (error) { throw new ErrorResponse(error); }
};


// `createCustomer` (POST /customers) ist am 09.08.2026 entfallen — WV-11,
// Entscheid Alex. Die Funktion hatte schon vorher keinen Aufrufer (unabhaengig
// von der CreateCustomerDialog-Loeschung am 07.08.), und die Route selbst ist
// jetzt stillgelegt: ein Aufruf haette ab sofort 405 geliefert. Kunden-Anlage
// laeuft ueber `saveCustomer` (Wizard) bzw. den Buchungs-Pfad.

// Wizard-spezifischer "Nur Daten speichern"-Pfad. Akzeptiert Wizard-Slugs
// (deutsche Labels fuer marital_status / occupation) und delegiert serverseitig
// an CustomerFieldMapper::mapAndUpsert (Single Source der Wizard-Slug-
// Uebersetzung — identisch zum Buchungs-Pfad). Der generische POST /customers
// bleibt davon unberuehrt.
const saveCustomerFromWizard = async (
  customerData: Record<string, unknown>,
): Promise<ApiResponse<{ id: number }>> => {
  try {
    const { data } = await network.post<ApiResponse<{ id: number }>>('/callcenter/save-customer', customerData);
    return data;
  } catch (error) { throw new ErrorResponse(error); }
};

const bookMeeting = async (bookingData: Record<string, unknown>): Promise<ApiResponse<{ meeting_id: number; contact_id: string; assigned_vp: { name: string; email: string; avatar_url: string | null; role: string }; zoom_link: string; pool_name: string }>> => {
  try {
    const { data } = await network.post<ApiResponse<{ meeting_id: number; contact_id: string; assigned_vp: { name: string; email: string; avatar_url: string | null; role: string }; zoom_link: string; pool_name: string }>>('/book-meeting', bookingData);
    return data;
  } catch (error) { throw new ErrorResponse(error); }
};

const getAvailability = async (
  poolId: number,
  date: string,
  userId?: number,
  // Puffer-Kontext (docs/tasks/pool-dauer-puffer.md): 'self' = VP-fuer-mich
  // (kein Puffer, Self-Dauer), 'cc' = Callcenter/Express (Pool-Dauer + Puffer).
  // 'internal' = Themen-Buchung aus dem Case (Decision #112): Pool-Semantik wie
  // 'cc', aber ohne Buchungshorizont — der interne Weg hat seit 03.08.2026
  // keinen.
  bookingType?: 'cc' | 'self' | 'internal',
): Promise<AvailabilityResponse> => {
  try {
    const params: Record<string, string | number> = { pool_id: poolId, date };
    if (userId) params.user_id = userId;
    if (bookingType) params.booking_type = bookingType;
    const { data } = await network.get<AvailabilityResponse>('/callcenter/availability', { params });
    return data;
  } catch (error) { throw new ErrorResponse(error); }
};

/**
 * Verfuegbarkeit fuer einen ZEITRAUM in EINEM Aufruf — speist das Ausgrauen
 * leerer Tage im Monatskalender (feedback-fixes-2026-07-22 Punkt 8).
 * Die Tagesabfrage bleibt die Quelle der Slot-Liste.
 */
const getAvailabilityDays = async (
  poolId: number,
  from: string,
  to: string,
  userId?: number,
  bookingType?: 'cc' | 'self' | 'internal',
): Promise<AvailabilityRangeResponse> => {
  try {
    const params: Record<string, string | number> = { pool_id: poolId, from, to };
    if (userId) params.user_id = userId;
    if (bookingType) params.booking_type = bookingType;
    const { data } = await network.get<AvailabilityRangeResponse>('/callcenter/availability/days', { params });
    return data;
  } catch (error) { throw new ErrorResponse(error); }
};

const bookForMe = async (bookingData: Record<string, unknown>): Promise<ApiResponse<{ meeting_id: number; contact_id: string; assigned_vp: { name: string; email: string; avatar_url: string | null; role: string }; zoom_link: string; scheduled_at: string; contact_name: string }>> => {
  try {
    const { data } = await network.post<ApiResponse<{ meeting_id: number; contact_id: string; assigned_vp: { name: string; email: string; avatar_url: string | null; role: string }; zoom_link: string; scheduled_at: string; contact_name: string }>>('/callcenter/book-for-me', bookingData);
    return data;
  } catch (error) { throw new ErrorResponse(error); }
};

export interface RouteLeadPayload {
  /** `bindung` seit 07.08.2026 — s. store/callcenter/buchungsmodus.ts. */
  mode: Buchungsmodus;
  // Leadherkunft — BE-Pflichtfeld; steuert das KreditLeads-Routing.
  lead_source: string;
  net_salary?: number | null;
  partner_net_salary?: number | null;
  additional_income?: number | null;
  partner_additional_income?: number | null;
  birth_year?: number | null;
  partner_birth_year?: number | null;
  /**
   * Kontaktmerkmale fuer das RC-Auffangnetz (Decision #171, 19.08.2026). Sie
   * routen NICHTS — der Pool haengt unveraendert an Einkommen/Alter/Bindung.
   * Sie beantworten allein, ob dieser Anrufer ein gepinnter RC-Kunde ist und
   * ob sein Alt-VP im Fenster etwas frei hat. Telefon/Mail statt `customer_id`,
   * weil die RC-Weiche seit #166 R4 am Kontakt-Match haengt.
   */
  phone?: string | null;
  email?: string | null;
}

export interface RouteLeadResponse {
  mode: Buchungsmodus;
  // `horizont_tage`: Callcenter-Horizont des Pools (E1, 22.10.2026).
  pool: { id: number; slug: string; name: string; horizont_tage?: number } | null;
  assigned_user: { id: number; name: string } | null;
  /**
   * RC-Auffangnetz (Decision #171): `null` = kein Fall fuer die Weiche (kein
   * Pin, oder der Alt-VP hat frei) ⇒ Anzeige unveraendert wie nach #168.
   * Gesetzt = Pool-Kalender + Banner.
   */
  rc_weiche?: RcWeiche | null;
}

// Decision #61: Pool-Klassifikation + ggf. VP-Vorschau aus Lead-Daten.
// Bei mode=pool macht das Backend einen WRR-Lauf (Vorschau-VP). Beim Submit
// laeuft die finale Buchung ueber bookMeeting/bookForMe — dort wird die
// VP-Allokation final festgezogen.
const routeLead = async (payload: RouteLeadPayload): Promise<RouteLeadResponse> => {
  try {
    const { data } = await network.post<RouteLeadResponse>('/callcenter/route-lead', payload);
    return data;
  } catch (error) { throw new ErrorResponse(error); }
};

// ─── Paket 2 (Alex 01.10.2026): Termin ändern / stornieren / neu ansetzen ───
//
// Eng geschnittene Wizard-Endpoints. Jeder Aufruf trägt den `stand` aus der
// Kontaktsuche (E8) — der Server handelt nur, wenn Termin/Fall noch genau so
// stehen, sonst 409 KONTEXT_VERALTET. `idempotency_key` schützt gegen
// Doppelklick und Wiederholung (E9).

const terminZeiten = async (meetingId: number, stand: string): Promise<WizardZeitenAntwort> => {
  try {
    const { data } = await network.get<WizardZeitenAntwort>(
      `/callcenter/termin-aktion/termin/${meetingId}/zeiten`, { params: { stand } },
    );
    return data;
  } catch (error) { throw new ErrorResponse(error); }
};

const terminVerschieben = async (
  meetingId: number, stand: string, scheduledAt: string, idempotencyKey: string,
): Promise<{ ergebnis: 'verschoben'; scheduled_at: string; bisher: string }> => {
  try {
    const { data } = await network.post<{ ergebnis: 'verschoben'; scheduled_at: string; bisher: string }>(
      `/callcenter/termin-aktion/termin/${meetingId}/verschieben`,
      { stand, scheduled_at: scheduledAt, idempotency_key: idempotencyKey },
    );
    return data;
  } catch (error) { throw new ErrorResponse(error); }
};

const terminStornieren = async (
  meetingId: number, stand: string, grund: string, idempotencyKey: string,
): Promise<{ ergebnis: 'storniert' }> => {
  try {
    const { data } = await network.post<{ ergebnis: 'storniert' }>(
      `/callcenter/termin-aktion/termin/${meetingId}/stornieren`,
      { stand, grund, idempotency_key: idempotencyKey },
    );
    return data;
  } catch (error) { throw new ErrorResponse(error); }
};

const fallZeiten = async (caseId: number, stand: string): Promise<WizardZeitenAntwort> => {
  try {
    const { data } = await network.get<WizardZeitenAntwort>(
      `/callcenter/termin-aktion/fall/${caseId}/zeiten`, { params: { stand } },
    );
    return data;
  } catch (error) { throw new ErrorResponse(error); }
};

const fallNeuansetzen = async (
  caseId: number, stand: string, scheduledAt: string, slotToken: string, idempotencyKey: string,
): Promise<{ ergebnis: 'neuansetzung'; scheduled_at: string }> => {
  try {
    const { data } = await network.post<{ ergebnis: 'neuansetzung'; scheduled_at: string }>(
      `/callcenter/termin-aktion/fall/${caseId}/neuansetzung`,
      { stand, scheduled_at: scheduledAt, slot_token: slotToken, idempotency_key: idempotencyKey },
    );
    return data;
  } catch (error) { throw new ErrorResponse(error); }
};

const callcenterApi = {
  searchCustomerByContact,
  terminZeiten,
  terminVerschieben,
  terminStornieren,
  fallZeiten,
  fallNeuansetzen,
  saveCustomerFromWizard,
  bookMeeting,
  getAvailability,
  getAvailabilityDays,
  bookForMe,
  routeLead,
};

export default callcenterApi;
